/**
 * Titulares de los roles de miembro de un proyecto (SOU-481).
 *
 * Cada rol admitia exactamente una persona, en una columna de Project. Ahora viven en una
 * tabla puente y un rol puede tener varios titulares, pero las columnas se conservan: la
 * escalar lleva al PRIMERO y la tabla puente al conjunto. El backend las sirve juntas en
 * `projectMembers`, y el valor real de un rol es la union de las dos fuentes.
 *
 * Lo consumen la pantalla de miembros y el drawer del EP/OC, que necesita saber quien puede
 * ejecutar cada paso del flujo de firmas. Vive aqui para que no puedan divergir.
 */

/** Roles tal y como los nombra el backend al serializar `ProjectMemberRole`. */
export type ProjectMemberRoleName =
  | 'OperationExecutive'
  | 'DeputyProjectManager'
  | 'CommercialExecutive'
  | 'OperationsManager'
  | 'Controller';

/** Un titular servido por el backend dentro de `project.projectMembers`. */
export interface ProjectMemberRef {
  partnerId: number;
  role: ProjectMemberRoleName;
}

export const PROJECT_MEMBER_ROLES: ProjectMemberRoleName[] = [
  'OperationExecutive',
  'DeputyProjectManager',
  'CommercialExecutive',
  'OperationsManager',
  'Controller',
];

/**
 * Columna escalar historica de cada rol. Controller no tiene ninguna: es un rol nuevo y
 * nunca fue una de las cinco columnas. `AdministrationAgent` queda fuera a proposito, es un
 * rol fuera de uso cuya columna se conserva sin mostrarse.
 */
export const PROJECT_ROLE_SCALAR_FIELD: Record<ProjectMemberRoleName, string | undefined> = {
  OperationExecutive: 'operationExecutiveId',
  DeputyProjectManager: 'deputyProjectManagerId',
  CommercialExecutive: 'commercialExecutiveId',
  OperationsManager: 'operationsManagerId',
  Controller: undefined,
};

/**
 * Campo de lista que la pantalla ENVIA al guardar. El backend los acepta en el patch pero
 * no los devuelve: al leer, los titulares llegan en `projectMembers`.
 */
export const PROJECT_ROLE_LIST_FIELD: Record<ProjectMemberRoleName, string> = {
  OperationExecutive: 'operationExecutiveIds',
  DeputyProjectManager: 'deputyProjectManagerIds',
  CommercialExecutive: 'commercialExecutiveIds',
  OperationsManager: 'operationsManagerIds',
  Controller: 'controllerIds',
};

/** Etiquetas de rol tal y como las ve el usuario en la pantalla de miembros. */
export const PROJECT_ROLE_LABEL: Record<ProjectMemberRoleName, string> = {
  OperationExecutive: 'Ejecutivo de Operaciones',
  DeputyProjectManager: 'Gerente de Administración',
  CommercialExecutive: 'Ejecutivo Comercial',
  OperationsManager: 'Jefe de Operaciones',
  Controller: 'Controller',
};

/**
 * Titulares de un rol, sin duplicados.
 *
 * Si el proyecto ya lleva escrita la lista del rol, manda ella: significa que la pantalla la
 * edito y todavia no se ha vuelto a leer del servidor. Sin esa precedencia, quitar a un
 * titular lo resucitaria al instante desde la copia anterior de `projectMembers`.
 *
 * En cualquier otro caso el valor sale de la union de la columna escalar y la tabla puente.
 */
export function getProjectRoleHolderIds(project: any, role: ProjectMemberRoleName): number[] {
  const editedHolders = project?.[PROJECT_ROLE_LIST_FIELD[role]];
  if (Array.isArray(editedHolders)) {
    return Array.from(new Set<number>(editedHolders.filter((id: number) => !!id)));
  }

  const ids = new Set<number>();

  const scalarField = PROJECT_ROLE_SCALAR_FIELD[role];
  if (scalarField && project?.[scalarField]) {
    ids.add(project[scalarField]);
  }

  (project?.projectMembers || [])
    .filter((member: ProjectMemberRef) => member?.role === role && !!member?.partnerId)
    .forEach((member: ProjectMemberRef) => ids.add(member.partnerId));

  return Array.from(ids);
}

/** Todos los miembros del proyecto, sea cual sea su rol. */
export function getProjectMemberIds(project: any): number[] {
  const ids = new Set<number>();
  PROJECT_MEMBER_ROLES.forEach(role =>
    getProjectRoleHolderIds(project, role).forEach(id => ids.add(id)));
  return Array.from(ids);
}
